import React from 'react';

import { AppBar, Box, IconButton } from '@mui/material';
import { Skeleton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
// import { useTheme } from '@mui/material/styles';

import { buildNav } from '../../utils/transform-navigation';
import { Grow, StyledToolbar } from './styles';
import { Link as RouterLink } from 'react-router-dom';

import { Role } from '../../types';
import { TopNavigation } from './navigation.top';
import { SideNavigation } from './navigation.side';

interface NavigationProps {
  roles: Role[];
  children?: React.ReactNode;
}

// eslint-disable-next-line max-lines-per-function
export const Navigation: React.FC<NavigationProps> = ({
  roles,
  children,
  ...rest
}) => {
  // const theme = useTheme();
  const isBelow950px = useMediaQuery(`(max-width:950px)`); // Custom breakpoint

  const navElements = buildNav(roles);
  const dashboard = roles.find((role) => role.id === 'dashboard');

  return (
    <AppBar position="static" {...rest}>
      <StyledToolbar variant="dense">
        {isBelow950px ? ( // Show SideNavigation below 950px
          <>
            <SideNavigation navElements={navElements} />
            <AppIcon />
          </>
        ) : (
          // Show TopNavigation above 950px
          <>
            <AppIcon />
            <TopNavigation navElements={navElements} dashboard={dashboard} />
          </>
        )}
        <Grow />
        {children}
      </StyledToolbar>
    </AppBar>
  );
};

// eslint-disable-next-line max-lines-per-function
export const PlaceholderNavigation: React.FC = () => {
  const isBelow950px = useMediaQuery(`(max-width:950px)`);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: '#e9edf2',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: { md: 72, lg: 258 },
          flexDirection: 'column',
          px: { md: 1, lg: 2.5 },
          py: 2,
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        <Box
          sx={{
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: { md: 'center', lg: 'space-between' },
            mb: 2.5,
          }}
        >
          <Skeleton variant="rounded" width={34} height={34} animation={false} />
          <Skeleton
            variant="circular"
            width={18}
            height={18}
            animation={false}
            sx={{ display: { md: 'none', lg: 'block' } }}
          />
        </Box>
        {[...new Array(8)].map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              height: 42,
              px: { md: 0.5, lg: 1 },
            }}
          >
            <Skeleton variant="circular" width={18} height={18} animation={false} />
            <Skeleton
              variant="text"
              width={isBelow950px ? 0 : index % 3 === 0 ? 138 : 110}
              height={18}
              animation={false}
              sx={{ display: { md: 'none', lg: 'block' } }}
            />
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppBar position="static">
          <StyledToolbar variant="dense">
            <Box
              sx={{
                height: 44,
                width: { xs: 180, md: 300 },
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            >
              <Skeleton
                variant="rounded"
                width="100%"
                height="100%"
                animation={false}
              />
            </Box>
            <Grow />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={16} height={16} animation={false} />
              {!isBelow950px && (
                <Skeleton variant="text" width={72} height={20} animation={false} />
              )}
              <Skeleton variant="text" width={96} height={20} animation={false} />
              <Skeleton variant="circular" width={14} height={14} animation={false} />
            </Box>
          </StyledToolbar>
        </AppBar>
        <Box sx={{ p: { xs: 2.5, md: 4 }, display: 'grid', gap: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            }}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 0.5,
                p: 3,
                display: 'grid',
                gap: 1.25,
              }}
            >
              <Skeleton
                variant="text"
                width="54%"
                height={28}
                animation={false}
                sx={{ mx: 'auto' }}
              />
              <Skeleton variant="rounded" height={70} animation={false} />
              <Skeleton variant="text" width="44%" height={20} animation={false} />
            </Box>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 0.5,
                p: 3,
                display: 'grid',
                gap: 1.25,
              }}
            >
              <Skeleton variant="text" width="62%" height={28} animation={false} />
              <Skeleton variant="text" width="86%" height={22} animation={false} />
            </Box>
          </Box>
          <Skeleton variant="rounded" animation={false} height={640} />
        </Box>
      </Box>
    </Box>
  );
};

export const AppIcon: React.FC = () => {
  return (
    <IconButton size="large">
      <RouterLink to="/">
        <img
          alt=""
          src="/public/img/app_logo.png"
          width="36"
          height="auto"
          className=""
        />
      </RouterLink>
    </IconButton>
  );
};
